import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles } from '@material-ui/core';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PopupTopBar from './PopupTopBar';
import CustomPopupText from '../../shared/CustomPopupText';
import CustomPicker from '../../shared/CustomPicker';
import CsvUploadField from './CsvUploadField';
import CustomButton from '../../shared/CustomButton';
import { showProgress } from '../../../redux/actions/DealershipActions';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import Service from '../../../apis/service';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import { getStartAndEndDates, getExtractedErrorMsg, appendFormData, getInitialDates } from '../utils';
import OverrideUploadData from './OverrideUploadData';

const customStyles = {
  content: {
    height: '823px',
    width: '1070px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: '0px',
  },
};

const useStyles = makeStyles({
  popupMainContainer: {
    background: 'rgba(242, 244, 247, 1)',
    width: '100%',
    height: '83%',
  },
  publishDateField: {
    width: '438px',
    height: '44px',
    padding: '12px 12px',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '12px',
    lineHeight: '21px',
    marginTop: '12px',
    marginBottom: '12px',
    border: '1px solid rgba(228, 231, 236, 1)',
    borderRadius: '8px',
    background: 'rgba(254, 254, 254, 1)',
    boxSizing: 'border-box',
  },
  detailsContainer: {
    padding: '15px 15px 0px 15px',
    borderRadius: '8px',
  },
  evenItemParams: {
    height: '41px',
    padding: '12px',
    background: 'rgba(254, 254, 254, 1)',
    borderBottom: '1px solid rgba(228, 231, 236, 1)',
  },
  oddItemParams: {
    height: '41px',
    padding: '12px',
    background: 'rgba(242, 244, 247, 1)',
    borderBottom: '1px solid rgba(228, 231, 236, 1)',
  },
});

const UpdateDataPopup = ({ close }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const contestDetails = useSelector((state) => state.contestData.selectedContestDetails);
  const initialDate = getInitialDates(contestDetails?.type?.toLowerCase());
  const [data, setData] = useState(initialDate);
  const [csvFilename, setCsvFilename] = useState('');
  const [csvData, setCsvData] = useState(null);
  const [viewOverrideUploadData, setViewOverrideUploadData] = useState(false);
  useEffect(() => {
    if (data.forceOverride) {
      createCsvMetadata();
    }
  }, [data.forceOverride]);

  const checkParameterMatch = (param) =>
    csvData?.some(
      (item) => item.columnName === param.columnName && item.dataType === param.dataType && item.isDisplayed === false,
    );

  const handleDateChange = (label, date) => {
    let dateForCalculation = date;
    if (typeof date === 'string' && contestDetails?.type?.toLowerCase() === 'monthly') {
      const monthString = `${date} 1, 2000`;
      const monthIndex = new Date(Date.parse(monthString)).getMonth();
      const currentYear = new Date().getFullYear();
      dateForCalculation = new Date(currentYear, monthIndex, 1);
    } else if (typeof date === 'string' && contestDetails?.type?.toLowerCase() === 'yearly') {
      dateForCalculation = new Date(parseInt(date, 10), 0, 1);
    }

    const { startDate, endDate } = getStartAndEndDates(dateForCalculation, contestDetails?.type?.toLowerCase());
    setData({ ...data, [label]: date, startDate, endDate });
  };

  const handleDataChange = ({ target: { name, files } }) => {
    if (name === 'csvFile' && files.length > 0) {
      const fileData = files[0];

      setCsvFilename(fileData.name);
      setData((prevData) => ({
        ...prevData,
        [name]: fileData,
      }));

      getDatafromCsv(fileData);
    }
  };

  const createCsvMetadata = async () => {
    dispatch(showProgress(true));
    try {
      const entries = {
        id: contestDetails?.id,
        csvFile: data?.csvFile,
        startDate: data?.startDate,
        endDate: data?.endDate,
        forceOverride: data?.forceOverride,
      };
      const formData = appendFormData(entries);
      const res = await Service.post(apiEndpoints.CON_CSV_METADATA, formData);
      setCsvData(res);
      notifySuccess('Imported Successfully');
      close();
    } catch (error) {
      const errmsg = getExtractedErrorMsg(error);
      if (errmsg === 'dataExists') {
        setViewOverrideUploadData(true);
      } else {
        ErrorToast(error);
        close();
      }
    }
    dispatch(showProgress(false));
  };
  const handleClose = () => {
    setViewOverrideUploadData(false);
  };

  const handleForceOverride = () => {
    setData({ ...data, forceOverride: true });
  };
  const getDatafromCsv = async (fileData) => {
    dispatch(showProgress(true));
    try {
      const formData = new FormData();
      formData.append('csvFile', fileData);
      const res = await Service.post(apiEndpoints.CON_CREATE_CSV_METADATA, formData);
      setCsvData(res);
      notifySuccess('Successful');
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  return (
    <Modal isOpen onRequestClose={close} style={customStyles}>
      <>
        <PopupTopBar title="Upload Data" close={close} />
        <Grid container direction="column" className={classes.popupMainContainer}>
          <Grid item>
            <Grid container direction="row" className={classes.detailsContainer}>
              <Grid item xs={6}>
                <Grid container direction="column">
                  <Grid item>
                    <CustomPopupText text="Start Date" required />
                  </Grid>
                  <Grid item>
                    <CustomPicker
                      type={contestDetails?.type?.toLowerCase()}
                      data={data}
                      handleDateChange={handleDateChange}
                      customStyles={classes.publishDateField}
                      name="startDate"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid item>
                  <CustomPopupText text="Import Metadata" required />
                </Grid>
                <Grid item>
                  <CsvUploadField
                    name="csvFile"
                    handleChange={handleDataChange}
                    fileName={csvFilename}
                    accept=".csv"
                    height="44px"
                    width="482px"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container direction="column" className={classes.detailsContainer}>
              <Grid item xs={12}>
                <CustomPopupText
                  text="Score Parameters"
                  fontSize="16px"
                  fontWeight="700"
                  lineHeight="22.4px"
                  color="rgba(16, 24, 43, 1)"
                />
              </Grid>
              <Grid item xs={12} className={classes.detailsContainer}>
                {contestDetails?.scoreBreakupParameters?.map((param, index) => {
                  const isMatched = checkParameterMatch(param);
                  return (
                    <Grid
                      container
                      direction="row"
                      className={index % 2 === 0 ? classes.evenItemParams : classes.oddItemParams}
                      key={`params ${index + 1}`}
                    >
                      <Grid item xs={6}>
                        <CustomPopupText
                          text={`${param.columnName}(${param.dataType})`}
                          fontSize="12px"
                          fontWeight="500"
                          lineHeight="16.8px"
                          color="rgba(52, 64, 84, 1)"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Grid container direction="row">
                          {isMatched
                            ? csvData && (
                                <>
                                  <Grid item>
                                    <CheckCircleOutlineIcon fontSize="small" htmlColor="rgba(2, 122, 72, 1)" />
                                  </Grid>
                                  <Grid item>
                                    <CustomPopupText
                                      text="Parameter Matched"
                                      fontSize="12px"
                                      fontWeight="500"
                                      lineHeight="16.8px"
                                      color="rgba(2, 122, 72, 1)"
                                    />
                                  </Grid>
                                </>
                              )
                            : csvData && (
                                <>
                                  <Grid item>
                                    <ErrorOutlineIcon fontSize="small" htmlColor="rgba(217, 45, 32, 1)" />
                                  </Grid>
                                  <Grid item>
                                    <CustomPopupText
                                      text="Parameter name doesn't match"
                                      fontSize="12px"
                                      fontWeight="500"
                                      lineHeight="16.8px"
                                      color="rgba(217, 45, 32, 1)"
                                    />
                                  </Grid>
                                </>
                              )}
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent="flex-end"
          alignItems="center"
          className={classes.stickyFooter}
          style={{ padding: '10px' }}
        >
          <Grid item style={{ marginRight: '8px' }}>
            <CustomButton size="small" label="Cancel" type="secondary" onClick={close} />
          </Grid>
          <Grid item>
            <CustomButton size="small" type="primary" label="Import" disabled={!csvData} onClick={createCsvMetadata} />
          </Grid>
        </Grid>
        {viewOverrideUploadData && (
          <OverrideUploadData
            isOpen
            close={handleClose}
            handleOverride={handleForceOverride}
            startDate={data.startDate}
            type={contestDetails?.type?.toLowerCase()}
          />
        )}
      </>
    </Modal>
  );
};

UpdateDataPopup.propTypes = {
  close: PropTypes.func.isRequired,
};

export default UpdateDataPopup;
